import { MouseEvent, useState } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@design-system/components/typography/typography';
import { Post } from '../model/post.types';
import { formatPostDate } from '../lib/formatPostDate';
import { useCommunityContext } from '../lib/communityContext';
import { OwnPostActionsMenu } from './OwnPostActionsMenu';
import { OthersPostActionsMenu } from './OthersPostActionsMenu';
import { UserProfileModal } from './UserProfileModal';

type Props = {
  post: Post;
  pinnedCount: number;
  authorBlocked: boolean;
};

export const PostCardHeader = ({ post, pinnedCount, authorBlocked }: Props) => {
  const { canModerate } = useCommunityContext();
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const openProfile = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsProfileOpen(true);
  };

  const identity = (
    <>
      <img
        src={post.author.avatarUrl}
        alt={post.author.name}
        className="size-10 shrink-0 rounded-full object-cover"
      />
      <span className="flex min-w-0 flex-col">
        <span className="flex items-center gap-1.5">
          <Typography
            as="span"
            variant="body"
            size="large"
            className="font-black leading-[140%] text-black"
          >
            {post.author.name}
          </Typography>
          {post.author.isCreator && (
            <FontAwesomeIcon
              icon={faBadgeCheck}
              className="text-[#5F1ECC]"
              data-testid="creator-badge"
            />
          )}
          {authorBlocked && (
            <Typography
              as="span"
              variant="body"
              size="small"
              fontWeight="medium"
              className="text-[#E5322D]"
            >
              Blocked
            </Typography>
          )}
        </span>
        <Typography
          as="span"
          variant="body"
          size="small"
          fontWeight="normal"
          className="text-[#2C3548]"
        >
          {formatPostDate(post.createdAt)}
        </Typography>
      </span>
    </>
  );

  return (
    <header className="flex items-center gap-2.5">
      {canModerate ? (
        <>
          <button
            type="button"
            onClick={openProfile}
            aria-label={`View ${post.author.name}'s profile`}
            className="flex min-w-0 items-center gap-2.5 text-left"
            data-testid="post-author-profile-trigger"
          >
            {identity}
          </button>
          <UserProfileModal
            author={post.author}
            open={isProfileOpen}
            onClose={() => setIsProfileOpen(false)}
          />
        </>
      ) : (
        identity
      )}
      {canModerate && (
        <div className="ml-auto shrink-0">
          {post.author.isCreator ? (
            <OwnPostActionsMenu post={post} pinnedCount={pinnedCount} />
          ) : (
            <OthersPostActionsMenu
              post={post}
              pinnedCount={pinnedCount}
              authorBlocked={authorBlocked}
            />
          )}
        </div>
      )}
    </header>
  );
};
